/* eslint-disable @nx/enforce-module-boundaries -- deep `libs/xplat/api/dto` paths avoid the dto barrel -> tool-tracker -> xplat-api-services cycle */
import { DatePipe } from '@angular/common';
import type { ForemanEmployeeLabor } from '../../../../dto/src/lib/models/foreman/foreman-employee-labor.model';
import type { ForemanJobCrew } from '../../../../dto/src/lib/models/foreman/foreman-job-crew.model';
import type { ForemanJobDetail } from '../../../../dto/src/lib/models/foreman/foreman-job-detail.model';
import type {
  ForemanSaveEmployeeInOut,
  ForemanSaveEmployeeJob,
  ForemanSaveEmployeeLabor,
  ForemanSaveEquipmentDispatch,
  ForemanSaveEquipmentUsage,
  ForemanSaveJob,
  ForemanSaveJobCrew,
  ForemanSaveMaterialExpendable,
  ForemanSavePhase,
  ForemanSaveTask,
  ForemanSaveTimeSheet,
  ForemanSubmitTime,
} from '../../../../dto/src/lib/models/foreman/foreman-submit-time.model';
import moment from 'moment';
import { concatMap, map, Observable, of, throwError } from 'rxjs';
import type { ForemanJobService } from './foreman-job.service';
import { isPersistableForemanEmployeeInOut } from './foreman-employee-in-out.utils';
import { findIndividualTimeEntryTargetEmployee } from './foreman-individual-entry-employee.utils';
import { resolveShiftExceptionsForSubmit } from './foreman-shift-exceptions.utils';

/** Avoid `@ups/xplat/core` Guid here — that import participates in an Nx-reported services↔core↔dto cycle. */
function newSubmitTimeGuid(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value =
      char === 'x' ? random : [8, 9, 10, 11][Math.floor(Math.random() * 4)];
    return value.toString(16);
  });
}

function nullIfBlankForemanPhaseId(
  value: string | null | undefined
): string | null {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || trimmed === '00000000-0000-0000-0000-000000000000') {
    return null;
  }
  return trimmed;
}

function resolveForemanPhaseIdForSubmit(
  value: string | null | undefined,
  workOrders: Array<{ foremanPhaseID?: string | null; phaseID?: string | null }>
): string | null {
  const normalized = nullIfBlankForemanPhaseId(value);
  if (!normalized) {
    return null;
  }

  const byForemanId = workOrders.find(
    (workOrder) => String(workOrder.foremanPhaseID ?? '').trim() === normalized
  );
  if (byForemanId?.foremanPhaseID) {
    return String(byForemanId.foremanPhaseID).trim();
  }

  const byPhaseId = workOrders.find(
    (workOrder) => String(workOrder.phaseID ?? '').trim() === normalized
  );
  if (byPhaseId?.foremanPhaseID) {
    return String(byPhaseId.foremanPhaseID).trim();
  }

  return normalized;
}

type ForemanTimeSheetRow = NonNullable<ForemanJobDetail['timeSheets']>[number];

/**
 * Picks the `ForemanTimeSheet` row to update on individual submit. Prefer an
 * explicit shift match for the calendar day; otherwise reuse a legacy
 * null/zero-shift sheet for that day (pre-shift-column data).
 */
export function findExistingIndividualSubmitTimeSheet(
  timeSheets: ForemanJobDetail['timeSheets'] | undefined,
  selectedDate: Date,
  selectedShift: number
): ForemanTimeSheetRow | undefined {
  const selectedDateKey = moment(selectedDate).format('MM/DD/YYYY');
  const sameDay = (timeSheets ?? []).filter(
    (t) => moment(t.workDate).format('MM/DD/YYYY') === selectedDateKey
  );
  const sel = Number(selectedShift);
  const explicit = sameDay.find(
    (t) => t.shift != null && Number(t.shift) !== 0 && Number(t.shift) === sel
  );
  if (explicit) {
    return explicit;
  }
  return sameDay.find((t) => t.shift == null || Number(t.shift) === 0);
}

/** Same date/shift/crew rules as crew `submitTime` task payload (for submit + PDF). */
export function filterForemanDailyTasksForDateShiftCrew(
  tasks: ForemanJobDetail['tasks'] | undefined,
  selectedDate: Date,
  selectedShift: number | null | undefined,
  selectedForemanCrewID: string | null | undefined
): NonNullable<ForemanJobDetail['tasks']> {
  return (tasks ?? []).filter((t) => {
    const isTasks =
      t.bActive !== false &&
      !t.isNextShiftTask &&
      moment(t.dateWorked).format('MM/DD/YYYY') ===
        moment(selectedDate).format('MM/DD/YYYY') &&
      t.shift === selectedShift &&
      t.foremanJobCrewID === selectedForemanCrewID;

    const isNextShiftTasks =
      (t.bActive !== false &&
        t.isNextShiftTask &&
        selectedShift === 1 &&
        moment(t.dateWorked).format('MM/DD/YYYY') ===
          moment(selectedDate).format('MM/DD/YYYY') &&
        t.foremanJobCrewID === selectedForemanCrewID) ||
      (selectedShift === 2 &&
        t.bActive !== false &&
        t.isNextShiftTask &&
        moment(t.dateWorked).format('MM/DD/YYYY') ===
          moment(selectedDate).add(1, 'day').format('MM/DD/YYYY') &&
        t.foremanJobCrewID === selectedForemanCrewID);

    return isTasks || isNextShiftTasks;
  });
}

/** Same date/shift/crew rules as crew `submitTime` material payload (for submit + PDF). */
export function filterForemanMaterialExpendablesForDateShiftCrew(
  materialExpendables: ForemanJobDetail['materialExpendables'] | undefined,
  selectedDate: Date,
  selectedShift: number | null | undefined,
  selectedForemanCrewID: string | null | undefined
): NonNullable<ForemanJobDetail['materialExpendables']> {
  return (materialExpendables ?? []).filter(
    (m) =>
      m.bActive !== false &&
      moment(m.dateWorked).format('MM/DD/YYYY') ===
        moment(selectedDate).format('MM/DD/YYYY') &&
      (!selectedShift || m.shift === selectedShift) &&
      m.foremanJobCrewID === selectedForemanCrewID
  );
}

/** Maps job tasks to the foreman submit DTO (same rules as crew `submitTime`). */
export function buildForemanTasksSavePayload(
  tasks: ForemanJobDetail['tasks'] | undefined,
  foremanJobID: string,
  selectedDate: Date,
  selectedShift: number | null | undefined,
  selectedForemanCrewID: string | null | undefined
): ForemanSaveTask[] {
  return filterForemanDailyTasksForDateShiftCrew(
    tasks,
    selectedDate,
    selectedShift,
    selectedForemanCrewID
  ).map(
    (t) =>
      ({
        foremanDailyTaskID: t.foremanDailyTaskID,
        foremanTaskID: t.foremanTaskID,
        foremanJobID,
        dateWorked: moment(t.dateWorked).format('yyyy-MM-DD'),
        shift: t.shift,
        foremanJobCrewID: t.foremanJobCrewID,
        taskOrder: t.taskOrder,
        taskDescription: t.taskDescription,
        bActive: t.bActive,
        isFavorite: t.isFavorite,
        isNextShiftTask: t.isNextShiftTask,
      }) as ForemanSaveTask
  );
}

/** Maps material expendables to the foreman submit DTO (same rules as crew `submitTime`). */
export function buildForemanMaterialExpendablesSavePayload(
  materialExpendables: ForemanJobDetail['materialExpendables'] | undefined,
  foremanJobID: string,
  selectedDate: Date,
  selectedShift: number | null | undefined,
  selectedForemanCrewID: string | null | undefined
): ForemanSaveMaterialExpendable[] {
  return filterForemanMaterialExpendablesForDateShiftCrew(
    materialExpendables,
    selectedDate,
    selectedShift,
    selectedForemanCrewID
  ).map(
    (m) =>
      ({
        foremanMaterialExpendableID: m.foremanMaterialExpendableID,
        foremanJobID,
        dateWorked: moment(m.dateWorked).format('yyyy-MM-DD'),
        shift: m.shift,
        foremanJobCrewID: m.foremanJobCrewID,
        description: m.description,
        quantity: m.quantity,
        totalPrice: m.totalPrice,
        bActive: m.bActive,
      }) as ForemanSaveMaterialExpendable
  );
}

/** Maps job crews to the submit DTO, preserving explicit `bActive: false` on inactive crews. */
export function mapForemanJobCrewsToSavePayload(
  crews: ForemanJobCrew[] | undefined | null,
  foremanJobID: string,
  selectedShift: number | null
): ForemanSaveJobCrew[] {
  return (crews ?? []).map(
    (c) =>
      ({
        foremanJobCrewID: c.foremanJobCrewID,
        foremanJobID,
        jobCrewID: c.jobCrewID,
        name: c.name,
        shift: selectedShift,
        bActive: c.bActive !== false,
      }) as ForemanSaveJobCrew
  );
}

export type MapEmployeeLaborsToSavePayloadMode =
  | 'crewCrewBound'
  | 'individualEffectiveShift';

export interface MapEmployeeLaborsToSavePayloadOptions {
  selectedDate: Date;
  /** Individual mode: precomputed `MM/DD/YYYY` key; crew mode derives from `selectedDate` when omitted. */
  selectedDateKey?: string;
  selectedShift: number | null | undefined;
  /** Crew submit: labors must match this crew id. */
  selectedForemanCrewID?: string | null | undefined;
  mode: MapEmployeeLaborsToSavePayloadMode;
}

/**
 * Filters dispatch labors for the active day/shift (and crew, for crew submit)
 * and maps persistable in-outs to `ForemanSaveEmployeeLabor` rows.
 * Keeps crew vs individual shift rules aligned in one place.
 */
export function mapEmployeeLaborsToSavePayload(
  labors: ForemanEmployeeLabor[] | null | undefined,
  opts: MapEmployeeLaborsToSavePayloadOptions
): ForemanSaveEmployeeLabor[] {
  const selectedDateKey =
    opts.selectedDateKey ?? moment(opts.selectedDate).format('MM/DD/YYYY');

  const filtered = (labors ?? []).filter((l) => {
    if (moment(l.dateWorked).format('MM/DD/YYYY') !== selectedDateKey) {
      return false;
    }

    if (opts.mode === 'crewCrewBound') {
      if (l.bActive === false) {
        return false;
      }
      if (!(!opts.selectedShift || l.shift === opts.selectedShift)) {
        return false;
      }
      if (l.foremanJobCrewID !== opts.selectedForemanCrewID) {
        return false;
      }
    } else {
      const targetShift = opts.selectedShift ?? 1;
      const effectiveShift =
        l.shift === 0 || l.shift == null ? targetShift : l.shift;
      if (effectiveShift !== targetShift) {
        return false;
      }
      if (l.bActive === false) {
        // Deactivated in this session (hours/segments cleared) — must be sent
        // so the API persists the removal: the submit endpoint is a pure upsert
        // and leaves anything absent from the payload untouched in the DB.
        return !!l.isChanged;
      }
    }

    return !!(
      l.isAbsent ||
      (l.hours != null && l.hours > 0) ||
      l.foremanNotes?.trim() ||
      l.foremanExceptionNotes?.trim()
    );
  });

  return filtered.map((l) => {
    // Strip the client-side `inOuts` array from the payload — the API binds
    // time segments from `employeeInOuts` only. `localChangedAtUtcMs` and
    // `committedSignature` are device-only draft bookkeeping. `modifiedDate`
    // is server-owned and re-stamped on save.
    const {
      inOuts,
      localChangedAtUtcMs,
      committedSignature,
      modifiedDate,
      ...labor
    } = l;
    void localChangedAtUtcMs;
    void committedSignature;
    void modifiedDate;
    return {
      ...labor,
      dateWorked: moment(l.dateWorked).format('yyyy-MM-DD'),
      employeeInOuts: !inOuts
        ? []
        : inOuts.filter(isPersistableForemanEmployeeInOut).map(
            (i) =>
              ({
                foremanEmployeeInOutID: i.foremanEmployeeInOutID,
                foremanEmployeeJobID: i.foremanEmployeeJobID,
                foremanEmployeeLaborID: l.foremanEmployeeLaborID,
                bActive: i.bActive,
                timeIn: i.timeIn,
                timeOut: i.timeOut,
              }) as ForemanSaveEmployeeInOut
          ),
    } as ForemanSaveEmployeeLabor;
  });
}

function buildForemanSaveJob(
  job: ForemanJobDetail,
  customerEquipments: ForemanJobDetail['foremanCustomerEquipments']
): ForemanSaveJob {
  const customerEquipmentList =
    customerEquipments && customerEquipments.length > 0
      ? customerEquipments
      : job.foremanCustomerEquipment
        ? [job.foremanCustomerEquipment]
        : [];

  return {
    foremanJobID: job.foremanJobID,
    jobID: job.jobID,
    vpJobID: job.vpJobID,
    description: job.name,
    companyID: job.companyID,
    foremanJobStatusName: job.foremanJobStatusName,
    foremanJobStatusNote: job.foremanJobStatusNote,
    foremanCustomerEquipmentID:
      customerEquipmentList.length > 0
        ? customerEquipmentList[0].foremanCustomerEquipmentID
        : job.foremanCustomerEquipmentID,
    bActive: true,
  } as ForemanSaveJob;
}

export function submitTime(
  service: ForemanJobService
): Observable<ForemanJobDetail> {
  const selectedJob = service.selectedJob();
  const selectedDate = service.selectedDate();
  const selectedShift = service.selectedShift();
  const selectedForemanCrewID = service.selectedForemanCrewID();
  const customerEquipments =
    selectedJob.foremanCustomerEquipments?.length > 0
      ? selectedJob.foremanCustomerEquipments
      : selectedJob.foremanCustomerEquipment
        ? [selectedJob.foremanCustomerEquipment]
        : [];

  const url = `api/foreman/submit/${selectedJob.foremanJobID}/${new DatePipe(
    'en-US'
  ).transform(service.selectedDate(), 'yyyy-MM-dd')}`;

  const currentTimeSheet = service.currentTimeSheet();
  const shiftExceptionsContext = {
    selectedDate,
    selectedShift,
    selectedForemanJobCrewID: selectedForemanCrewID,
  };
  const shiftExceptions = resolveShiftExceptionsForSubmit(
    selectedJob,
    shiftExceptionsContext
  );

  const dto = {
    foremanJob: buildForemanSaveJob(selectedJob, customerEquipments),
    foremanCustomerEquipments: customerEquipments,
    timeSheet: {
      foremanTimeSheetID:
        currentTimeSheet?.foremanTimeSheetID || newSubmitTimeGuid(),
      foremanJobID: selectedJob.foremanJobID,
      workDate: service.selectedDate(),
      shift: selectedShift ? selectedShift : null,
      foremanJobCrewID: selectedForemanCrewID,
      customerFirstName: service.customerData()?.firstName,
      customerLastName: service.customerData()?.lastName,
      customerEmail: service.customerData()?.email,
      fileID: service.pdfFile()?.fileID,
      exceptions: shiftExceptions,
    } as ForemanSaveTimeSheet,
    // undefined when untouched -> JSON omits the envelope -> stored owner untouched (#30008)
    jobOwner: service.selectedJobOwner(),
    workOrders: selectedJob.workOrders.map((p) => {
      return {
        foremanPhaseID: p.foremanPhaseID,
        foremanJobID: service.selectedJob().foremanJobID,
        phaseID: p.phaseID,
        phaseName: p.phaseName,
        bActive: true,
      } as ForemanSavePhase;
    }),
    foremanCrews: mapForemanJobCrewsToSavePayload(
      selectedJob.foremanCrews,
      service.selectedJob().foremanJobID,
      selectedShift
    ),
    employees: (selectedJob.employees ?? []).map((e) => {
      return {
        foremanEmployeeJobID: e.foremanEmployeeJobID,
        foremanJobID: service.selectedJob().foremanJobID,
        employeeJobID: e.employeeJobID,
        manPowerReqDetailID: e.manPowerReqDetailID,
        foremanEmployeeID: e.foremanEmployeeID,
        bActive: e.bActive ?? true,
        startDate: e.startDate,
        endDate: e.endDate,
        rofDate: e.rofDate,
        lastDayOnSite: e.lastDayOnSite,
        shift: e.shift,
        craft: e.craft,
        foremanPhaseID: e.foremanPhaseID,
        dispatchStatusID: e.dispatchStatusID,
        employee: e.employee,
        foremanJobCrews: !e.foremanCrews
          ? []
          : e.foremanCrews.filter(
              (crew) =>
                !!selectedJob.foremanCrews?.find(
                  (jobCrew) =>
                    jobCrew.foremanJobCrewID === crew.foremanJobCrewID
                )
            ),
        employeeLabors: mapEmployeeLaborsToSavePayload(e.labors, {
          selectedDate,
          selectedShift,
          selectedForemanCrewID,
          mode: 'crewCrewBound',
        }),
      } as ForemanSaveEmployeeJob;
    }),
    equipment: (selectedJob.equipment ?? []).map(
      (e): ForemanSaveEquipmentDispatch => {
        return {
          foremanEquipmentDispatchID: e.foremanEquipmentDispatchID,
          equipmentDispatchID: e.equipmentDispatchID,
          foremanEquipmentID:
            e.foremanEquipmentID || e.foremanEquipment?.foremanEquipmentID,
          foremanJobID: service.selectedJob().foremanJobID,
          startDate: e.startDate
            ? moment(e.startDate).format('yyyy-MM-DD')
            : undefined,
          endDate: e.endDate
            ? moment(e.endDate).format('yyyy-MM-DD')
            : undefined,
          lastDayOnSite: e.lastDayOnSite
            ? moment(e.lastDayOnSite).format('yyyy-MM-DD')
            : undefined,
          bActive: e.bActive ?? true,
          foremanPhaseID: resolveForemanPhaseIdForSubmit(
            e.foremanPhaseID,
            selectedJob.workOrders ?? []
          ),
          shift: e.shift,
          equipment: e.foremanEquipment,
          altID: e.altID,
          foremanJobCrews: !e.foremanCrews
            ? []
            : e.foremanCrews.filter(
                (crew) =>
                  !!selectedJob.foremanCrews?.find(
                    (jobCrew) =>
                      jobCrew.foremanJobCrewID === crew.foremanJobCrewID
                  )
              ),
          equipmentUsages: (e.usages || [])
            .filter(
              (u) =>
                u.bActive !== false &&
                moment(u.dateUsed).format('MM/DD/YYYY') ===
                  moment(selectedDate).format('MM/DD/YYYY') &&
                (!selectedShift || u.shift === selectedShift) &&
                u.foremanJobCrewID === selectedForemanCrewID &&
                (u.notUsed || (u.amount != null && u.amount > 0))
            )
            .map((u) => {
              return {
                foremanEquipmentUsageID: u.foremanEquipmentUsageID,
                foremanEquipmentDispatchID: e.foremanEquipmentDispatchID,
                dateUsed: moment(u.dateUsed).format('yyyy-MM-DD'),
                foremanPhaseID: resolveForemanPhaseIdForSubmit(
                  u.foremanPhaseID,
                  selectedJob.workOrders ?? []
                ),
                shift: u.shift,
                foremanJobCrewID: u.foremanJobCrewID,
                quantity: u.quantity,
                amount: u.amount,
                foremanNotes: u.foremanNotes,
                bActive: u.bActive,
                notUsed: u.notUsed,
              } as ForemanSaveEquipmentUsage;
            }),
        } as ForemanSaveEquipmentDispatch;
      }
    ),
    tasks: buildForemanTasksSavePayload(
      selectedJob.tasks,
      selectedJob.foremanJobID,
      selectedDate,
      selectedShift,
      selectedForemanCrewID
    ),
    materialExpendables: buildForemanMaterialExpendablesSavePayload(
      selectedJob.materialExpendables,
      selectedJob.foremanJobID,
      selectedDate,
      selectedShift,
      selectedForemanCrewID
    ),
    safetyItems: [],
    pdfFile: service.pdfFile(),
  } as ForemanSubmitTime;

  return service.http.post(url, dto) as Observable<ForemanJobDetail>;
}

export function submitIndividualTime(
  service: ForemanJobService,
  jobs: ForemanJobDetail[],
  spartaEmployeeId?: string | null
): Observable<ForemanJobDetail[]> {
  if (!jobs.length) {
    return of([]);
  }

  const selectedDate = service.selectedDate() || new Date();
  const selectedShift = service.selectedShift() || 1;
  const pdfFile = service.pdfFile();
  const dateStr = new DatePipe('en-US').transform(selectedDate, 'yyyy-MM-dd');
  const selectedDateKey = moment(selectedDate).format('MM/DD/YYYY');

  const selectedForemanCrewID = service.selectedForemanCrewID();

  /**
   * One shared PDF is generated for all jobs; only the first POST should run
   * `SaveFile` — parallel `forkJoin` caused duplicate PK inserts on `dbo.File`.
   */
  return jobs.reduce(
    (acc$, job, index) =>
      acc$.pipe(
        concatMap((completed) => {
          const url = `api/foreman/submit/${job.foremanJobID}/${dateStr}`;

          const targetEmployee = findIndividualTimeEntryTargetEmployee(
            job,
            spartaEmployeeId
          );

          if (!targetEmployee) {
            return throwError(
              () =>
                new Error(
                  `Individual time submit: no employee row matched the signed-in user on job ${
                    job.foremanJobID || job.jobID || '(unknown)'
                  }.`
                )
            );
          }

          const customerEquipments =
            job.foremanCustomerEquipments?.length > 0
              ? job.foremanCustomerEquipments
              : job.foremanCustomerEquipment
                ? [job.foremanCustomerEquipment]
                : [];

          const existingTimeSheet = findExistingIndividualSubmitTimeSheet(
            job.timeSheets,
            selectedDate,
            selectedShift
          );

          const attachPdfBody = !!pdfFile && index === 0;

          const dto: ForemanSubmitTime = {
            foremanJob: buildForemanSaveJob(job, customerEquipments),
            foremanCustomerEquipments: customerEquipments,
            timeSheet: {
              foremanTimeSheetID:
                existingTimeSheet?.foremanTimeSheetID || newSubmitTimeGuid(),
              foremanJobID: job.foremanJobID,
              workDate: selectedDate,
              shift: selectedShift,
              foremanJobCrewID: null,
              customerFirstName: null,
              customerLastName: null,
              customerEmail: null,
              fileID: pdfFile?.fileID ?? null,
              exceptions: existingTimeSheet?.exceptions ?? null,
            } as ForemanSaveTimeSheet,
            workOrders: (job.workOrders || []).map(
              (p) =>
                ({
                  foremanPhaseID: p.foremanPhaseID,
                  foremanJobID: job.foremanJobID,
                  phaseID: p.phaseID,
                  phaseName: p.phaseName,
                  bActive: true,
                }) as ForemanSavePhase
            ),
            foremanCrews: [],
            employees: [
              {
                foremanEmployeeJobID: targetEmployee.foremanEmployeeJobID,
                foremanJobID: job.foremanJobID,
                employeeJobID: targetEmployee.employeeJobID,
                manPowerReqDetailID: targetEmployee.manPowerReqDetailID,
                foremanEmployeeID: targetEmployee.foremanEmployeeID,
                bActive: targetEmployee.bActive ?? true,
                startDate: targetEmployee.startDate,
                endDate: targetEmployee.endDate,
                rofDate: targetEmployee.rofDate,
                lastDayOnSite: targetEmployee.lastDayOnSite,
                shift: targetEmployee.shift,
                craft: targetEmployee.craft,
                dispatchStatusID: targetEmployee.dispatchStatusID,
                foremanPhaseID: targetEmployee.foremanPhaseID,
                employee: targetEmployee.employee,
                foremanJobCrews: [],
                employeeLabors: mapEmployeeLaborsToSavePayload(
                  targetEmployee.labors,
                  {
                    selectedDate,
                    selectedDateKey,
                    selectedShift,
                    mode: 'individualEffectiveShift',
                  }
                ),
              } as ForemanSaveEmployeeJob,
            ],
            equipment: [],
            tasks: buildForemanTasksSavePayload(
              job.tasks,
              job.foremanJobID,
              selectedDate,
              selectedShift,
              selectedForemanCrewID
            ),
            materialExpendables: buildForemanMaterialExpendablesSavePayload(
              job.materialExpendables,
              job.foremanJobID,
              selectedDate,
              selectedShift,
              selectedForemanCrewID
            ),
            safetyItems: [],
            ...(attachPdfBody ? { pdfFile } : {}),
          };

          return (
            service.http.post(url, dto) as Observable<ForemanJobDetail>
          ).pipe(map((detail) => [...completed, detail]));
        })
      ),
    of([] as ForemanJobDetail[])
  );
}
